import {Component, Inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {DOCUMENT} from '@angular/common';
import {Observable, of, Subject, zip} from 'rxjs';
import {filter, switchMap, takeUntil} from 'rxjs/operators';
import {
  ProfileModel,
  NavigationMetadataTextsModel,
  AdaptDockedPanelService,
  MenuItem,
  AdaptNavigationComponent
} from '@bmc-ux/adapt-angular';
import {MenuItemsLink, MenuItemsName, SubMenuItems} from './models/navigation';
import {OktaUser, User} from './models/user';
import {UserService} from './services/user.service';
import {OktaAuthService} from './services/okta-auth.service';
import {LoaderService} from './services/loader.service';
import {GeoInfoService} from './services/geo-info.service';
import {GeoInfo} from './models/geo-info';
import {NavigationEnd, Router, RouterEvent} from '@angular/router';
import {PageAccessInfo} from './models/page-access-info';
import {PageAccessStorageService} from './store/page-access-storage.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.sass']
})
export class AppComponent implements OnInit, OnDestroy {
  @ViewChild('adaptNavigation') adaptNavigation: AdaptNavigationComponent;

  public isOutageServer = false;
  public isLoadedUserInfo = false;
  public loaderConfig = null;
  public isInternetExplorer = false;
  public navBarMetadata: NavigationMetadataTextsModel = {
    companyName: 'BMC Helix',
    productName: 'Compatibility Matrix Admin',
    logoClass: 'logo-bmc-helix',
    route: '/',   // - use only one of 'action', 'route', 'link'
    hideMobileLogo: false
  };

  public navBarMenuItems: MenuItem[] = [
    {
      id: 'navbar-menu-search',
      label: '',
      name: MenuItemsName.SEARCH,
      link: '/'
    },
    {
      id: 'navbar-menu-request',
      label: '',
      name: MenuItemsName.REQUEST,
      hide: true,
      link: MenuItemsLink.REQUEST,
    },
    {
      id: 'navbar-menu-report',
      label: '',
      name: MenuItemsName.REPORT,
      routerLink: 'report',
    },
    {
      id: 'navbar-menu-contact',
      label: '',
      name: MenuItemsName.CONTACT,
      link: MenuItemsLink.CONTACT,
    },
    {
      id: 'navbar-menu-help',
      label: '',
      name: MenuItemsName.HELP,
      link: MenuItemsLink.HELP,
      target: '_blank'
    },
    {
      id: 'navbar-menu-admin',
      label: '',
      name: MenuItemsName.ADMIN,
      link: '/',
      hide: true,
      subMenu: [
        {
          id: 'navbar-menu-add-record',
          label: '',
          name: SubMenuItems.ADD_RECORD,
          routerLink: 'administration/master-records',
        },
        {
          id: 'navbar-menu-admin-feedback',
          label: '',
          name: SubMenuItems.FEEDBACK,
          routerLink: 'administration/feedback-report',
        },
        {
          id: 'navbar-menu-admin-email',
          label: '',
          name: SubMenuItems.EMAIL,
          routerLink: 'administration/email-management',
        }
      ]
    },
  ];

  public navBarUserProfile: ProfileModel = {
    userName: '',
    desc: 'Signed in as',
    header: ' ',
    inverted: false,
    typeLong: true,
    hideAvatar: false,
    view: {},
    signOut: {
      title: 'Sign out',
      action: () => {
        this.oktaAuth.logout();
      }
    },
    menu: []
  };

  private onComponentDestroyed$: Subject<void> = new Subject();

  constructor(@Inject(DOCUMENT) private document: Document,
              private userService: UserService,
              private oktaAuth: OktaAuthService,
              private geoInfoService: GeoInfoService,
              private adaptDockedPanelService: AdaptDockedPanelService,
              private loaderService: LoaderService,
              private pageAccessInfoStorageService: PageAccessStorageService,
              private router: Router) {
    router.events.pipe(
      filter((routerEvent: RouterEvent) => routerEvent instanceof NavigationEnd),
      takeUntil(this.onComponentDestroyed$),
    ).subscribe((routerEvent: RouterEvent) => {
      // URL - from OKTA we get redirect URI like this:
      // /implicit/callback#state={some_token}&error=access_denied&error_description=User+is+not+assigned+to+the+client+application
      if (routerEvent.url.includes('access_denied')) {
        this.router.navigate(['/access_denied']);
      }
    });
  }

  ngOnInit(): void {
    this.loaderService.onToggleLoader.pipe(
      takeUntil(this.onComponentDestroyed$),
    ).subscribe((loaderConfig): void => {
      this.loaderConfig = loaderConfig;
    });
    // IE11 or above Boolean
    const ie11AndAbove = navigator.userAgent.indexOf('Trident') !== -1 && navigator.userAgent.indexOf('MSIE') === -1;

    // IE10 or below Boolean
    const ie10AndBelow = navigator.userAgent.indexOf('MSIE') !== -1;

    this.isInternetExplorer = ie11AndAbove || ie10AndBelow;

    this.userService.getOktaUserInfo()
      .pipe(
        switchMap((user: OktaUser): Observable<[OktaUser, User, GeoInfo]> => {
          if (user) {
            this.navBarMenuItems.forEach((menuItem: MenuItem): void => {
              if (user.isUserAdmin && menuItem.name === MenuItemsName.ADMIN) {
                menuItem.hide = false;
              }
              if (!user.isUserAdmin && menuItem.name === MenuItemsName.REQUEST) {
                menuItem.hide = false;
              }
            });
            this.navBarUserProfile.userName = `${user.name}`;
            this.navBarUserProfile = Object.assign({}, this.navBarUserProfile);

            return zip(
              of(user),
              this.userService.getUser(),
              this.geoInfoService.getGeoInfo()
            );
          }

          return of(null);
        }),
        takeUntil(this.onComponentDestroyed$)
      ).subscribe(([oktaUser, user, geoInfo]: [OktaUser, User, GeoInfo]): void => {
        this.loaderService.toggleLoader(false);

        if (!user && window.location.pathname !== '/access_denied') {
          this.isOutageServer = true;
          return;
        }

        if (oktaUser && user) {
          this.isLoadedUserInfo = true;
          this.pageAccessInfoStorageService.togglePageAccessInfo(
            new PageAccessInfo(user, oktaUser, geoInfo, true)
          );
        } else {
          this.router.navigate(['access_denied']);
          return;
        }

        if (geoInfo && geoInfo.hasOwnProperty('blocked') && geoInfo.blocked === false) {
          this.renderGoogleAnalyticsScripts();
        }

        if (window.location.pathname !== '/') {
          this.loaderService.toggleLoader(false);
        }
      });

    if (this.isInternetExplorer) {
      this.loaderService.toggleLoader(false);
    }
  }

  ngOnDestroy(): void {
    this.onComponentDestroyed$.next();
    this.onComponentDestroyed$.complete();
    this.onComponentDestroyed$.unsubscribe();
  }

  private renderGoogleAnalyticsScripts(): void {
    const $bmcGtmScripts = document.getElementById('bmc-gtm-script');

    if ($bmcGtmScripts) {
      let bmcGtmScriptsContent;
      $bmcGtmScripts.setAttribute('data-show-gtm', 'true');

      bmcGtmScriptsContent = '(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({\'gtm.start\': \n';
      bmcGtmScriptsContent += 'new Date().getTime(),event:\'gtm.js\'});var f=d.getElementsByTagName(s)[0], \n';
      bmcGtmScriptsContent += 'j=d.createElement(s),dl=l!=\'dataLayer\'?\'&l=\'+l:\'\';j.async=true;j.src= \n';
      bmcGtmScriptsContent += '\'https://www.googletagmanager.com/gtm.js?id=\'+i+dl;f.parentNode.insertBefore(j,f); \n';
      bmcGtmScriptsContent += '})(window,document,\'script\',\'dataLayer\',\'GTM-WPN2PQ9V\');';

      $bmcGtmScripts.innerHTML = bmcGtmScriptsContent;
    }
  }
}
